import { string, z } from 'zod';
import strings from './strings';

// validates a USA postal code. e.g. 12345 or 12345-1234
const isValidUsaPostalCode = (value: string): boolean => {
  const postalCodeRegex = /^[0-9]{5}(?:-[0-9]{4})?$/;
  return postalCodeRegex.test(value);
};

// validates a Canadian postal code. e.g. A1A 1A1
const isValidCanadaPostalCode = (value: string): boolean => {
  const postalCodeRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
  return postalCodeRegex.test(value);
};

// validates latitude. e.g. -90 <= latitude <= 90
const isValidLatitude = (value?: string): boolean => {
  if (value === '' || value === undefined) {
    return true;
  }
  const parsedFloat = parseFloat(value);
  return parsedFloat >= -90 && parsedFloat <= 90;
};

// validates longitude. e.g. -180 <= longitude <= 180
const isValidLongitude = (value?: string): boolean => {
  if (value === '' || value === undefined) {
    return true;
  }
  const parsedFloat = parseFloat(value);
  return parsedFloat >= -180 && parsedFloat <= 180;
};

// zod schema for address form validation
const americanZipCodeValidationSchema = z.object({
  country: z.literal('US'),
  zip: string().refine(
    (postalCode) => isValidUsaPostalCode(postalCode),
    strings.errors.zipUs,
  ),
});

const canadianPostalCodeValidationSchema = z.object({
  country: z.literal('CA'),
  zip: string().refine(
    (postalCode) => isValidCanadaPostalCode(postalCode),
    strings.errors.zipCa,
  ),
});

// discriminates the payee field to determine which billing validation schema to use
const conditionalPostalCodeValidationSchema = z.discriminatedUnion('country', [
  americanZipCodeValidationSchema,
  canadianPostalCodeValidationSchema,
]);

// zod schema for address form validation
const baseAddressFormValidationSchema = z.object({
  street: string().min(1, strings.errors.street),
  unit: string(),
  city: string().min(1, strings.errors.city),
  state: string().min(1, strings.errors.state),
  latitude: string()
    .optional()
    .refine((value) => isValidLatitude(value), strings.errors.latitude),
  longitude: string()
    .optional()
    .refine((value) => isValidLongitude(value), strings.errors.longitude),
});

// combines the customer and billing validation schemas into one schema
export const addressFormValidationSchema = z.intersection(
  baseAddressFormValidationSchema,
  conditionalPostalCodeValidationSchema,
);

export type AddressFormValidationType = z.TypeOf<
  typeof addressFormValidationSchema
>;

export default addressFormValidationSchema;
